import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Spinner,
  Form,
} from 'react-bootstrap';
import { AlertCircle, CheckCircle2, ClipboardList, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import DashboardLayout from '../components/DashboardLayout';
import { fetchProfileLookups, hydrateProfile } from '../lib/profileLookups';
import { formatRoleLabel, getCriteriaScope } from '../lib/accessRules';
import { fetchKriteriaList, mapCriteriaForAccess } from '../lib/kriteriaMetadata';

function Dashboard() {
  const navigate = useNavigate();

  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [prodiList, setProdiList] = useState([]);
  const [selectedProdiId, setSelectedProdiId] = useState('');
  const [criteriaStatus, setCriteriaStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUser = async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    setAuthUser(user);

    const { data: profileData, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error || !profileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (!profileData.is_active) {
      setErrorMsg('Akun nonaktif.');
      setLoading(false);
      return;
    }

    try {
      const lookups = await fetchProfileLookups(supabase);
      setProdiList(lookups.prodiList || []);
      const hydratedProfile = hydrateProfile(profileData, lookups);
      setProfile(hydratedProfile);

      const dashboardProdiId =
        hydratedProfile.role === 'superadmin'
          ? selectedProdiId || lookups.prodiList?.[0]?.id || null
          : hydratedProfile.prodiData?.id || null;

      if (hydratedProfile.role === 'superadmin' && dashboardProdiId && !selectedProdiId) {
        setSelectedProdiId(dashboardProdiId);
      }

      await loadCriteriaStatus(hydratedProfile, dashboardProdiId);
    } catch (lookupError) {
      setErrorMsg(lookupError.message);
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  const loadCriteriaStatus = async (profileScope = profile, fallbackProdiId = null) => {
    const scopedProdiId = profileScope?.prodiData?.id || fallbackProdiId || null;
    const dynamicCriteria = await fetchKriteriaList(supabase, {
      prodiId: scopedProdiId,
    });
    const criteriaConfig = mapCriteriaForAccess(dynamicCriteria).map((criteria) => ({
      code: criteria.kode_kriteria,
      label: criteria.nama_kriteria,
      route:
        profileScope?.role === 'superadmin' && fallbackProdiId
          ? `${criteria.route}?prodi=${fallbackProdiId}`
          : criteria.route,
      criteriaId: criteria.id,
    }));

    const criteriaScope = getCriteriaScope(profileScope, criteriaConfig);

    const results = await Promise.all(
      criteriaScope.map(async (criteria) => {
        let query = supabase
          .schema('akreditasi')
          .from('kriteria_data')
          .select('id', { count: 'exact', head: true })
          .eq('kriteria_id', criteria.criteriaId)
          .eq('is_deleted', false);

        if (scopedProdiId) {
          query = query.eq('prodi_id', scopedProdiId);
        }

        const { count, error } = await query;

        if (error) {
          throw new Error(`Gagal menghitung data ${criteria.code}: ${error.message}`);
        }

        return {
          ...criteria,
          total: count || 0,
          ready: (count || 0) > 0,
        };
      })
    );

    setCriteriaStatus(results);
  };

  const readyCriteria = criteriaStatus.filter((item) => item.ready);
  const incompleteCriteria = criteriaStatus.filter((item) => !item.ready);
  const isUserSuperadmin = profile?.role === 'superadmin';
  const selectedProdi = prodiList.find((item) => item.id === selectedProdiId);
  const readinessPercent =
    criteriaStatus.length > 0
      ? Math.round((readyCriteria.length / criteriaStatus.length) * 100)
      : 0;

  const handleDashboardProdiChange = async (prodiId) => {
    setSelectedProdiId(prodiId);
    if (profile) {
      setLoading(true);
      await loadCriteriaStatus(profile, prodiId);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout
        title={isUserSuperadmin ? 'Dashboard' : 'Dashboard Admin Prodi'}
        subtitle="Memuat data pengguna..."
        profile={profile}
        activeMenu="dashboard"
      >
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={isUserSuperadmin ? 'Dashboard' : 'Dashboard Admin Prodi'}
      subtitle="Sistem Informasi Akreditasi Politeknik Negeri Manado"
      profile={profile}
      activeMenu="dashboard"
    >
      <Container fluid className="p-0">
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        <Card className="hero-card-v2 compact-hero mb-3">
          <Card.Body>
            <div>
              <div className="hero-eyebrow-v2">Dashboard Akreditasi</div>
              <h2 className="hero-title-v2">{profile?.nama_lengkap || 'Pengguna'}</h2>
            </div>
            <div className="hero-status-panel">
              <span>Progress</span>
              <strong>{readinessPercent}%</strong>
            </div>
          </Card.Body>
        </Card>

        <div className="metric-grid-v2 mb-3">
          <div className="metric-card-v2">
            <div className="metric-icon-v2 blue"><ClipboardList size={20} /></div>
            <div>
              <span>Total Kriteria</span>
              <strong>{criteriaStatus.length}</strong>
            </div>
          </div>
          <div className="metric-card-v2">
            <div className="metric-icon-v2 green"><CheckCircle2 size={20} /></div>
            <div>
              <span>Sudah Terisi</span>
              <strong>{readyCriteria.length}</strong>
            </div>
          </div>
          <div className="metric-card-v2">
            <div className="metric-icon-v2 amber"><AlertCircle size={20} /></div>
            <div>
              <span>Perlu Dilengkapi</span>
              <strong>{incompleteCriteria.length}</strong>
            </div>
          </div>
          <div className="metric-card-v2">
            <div className="metric-icon-v2 cyan"><GraduationCap size={20} /></div>
            <div>
              <span>Scope Prodi</span>
              <strong>
                {isUserSuperadmin
                  ? selectedProdi
                    ? `${selectedProdi.jenjang} ${selectedProdi.nama_prodi}`
                    : `${prodiList.length} prodi`
                  : profile?.prodi_display || '-'}
              </strong>
            </div>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col xl={8}>
            <Card className="content-card-v2">
              <Card.Body>
                <div className="section-head-v2">
                  <div>
                    <h4 className="section-title-v2">Informasi Pengguna</h4>
                  </div>
                  <Badge bg="primary">{formatRoleLabel(profile)}</Badge>
                </div>

                {isUserSuperadmin && (
                  <Form.Group className="mt-3">
                    <Form.Label>Scope Dashboard</Form.Label>
                    <Form.Select
                      value={selectedProdiId}
                      onChange={(e) => handleDashboardProdiChange(e.target.value)}
                    >
                      {prodiList.map((prodi) => (
                        <option value={prodi.id} key={prodi.id}>
                          {prodi.jenjang} - {prodi.nama_prodi}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <div className="info-box-v2">
                      <div className="info-label-v2">Email</div>
                      <div className="info-value-v2">{authUser?.email || '-'}</div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="info-box-v2">
                      <div className="info-label-v2">Nama Lengkap</div>
                      <div className="info-value-v2">{profile?.nama_lengkap || '-'}</div>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="info-box-v2">
                      <div className="info-label-v2">Role</div>
                      <div className="info-value-v2">{formatRoleLabel(profile)}</div>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="info-box-v2">
                      <div className="info-label-v2">Status</div>
                      <div className="info-value-v2">
                        {profile?.is_active ? 'Aktif' : 'Nonaktif'}
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="info-box-v2">
                      <div className="info-label-v2">Prodi</div>
                      <div className="info-value-v2">
                        {isUserSuperadmin
                          ? selectedProdi
                            ? `${selectedProdi.jenjang} - ${selectedProdi.nama_prodi}`
                            : `${prodiList.length} program studi`
                          : profile?.prodi_display || profile?.prodi || '-'}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="content-card-v2">
              <Card.Body>
                <h4 className="section-title-v2 mb-3">Status Kesiapan Akreditasi</h4>
                <div className="readiness-summary-grid mb-3">
                  <div className="readiness-summary-box ready">
                    <div className="readiness-summary-label">Siap</div>
                    <div className="readiness-summary-value">
                      {readyCriteria.length} kriteria
                    </div>
                  </div>
                  <div className="readiness-summary-box attention">
                    <div className="readiness-summary-label">Perlu dilengkapi</div>
                    <div className="readiness-summary-value">
                      {incompleteCriteria.length} kriteria
                    </div>
                  </div>
                </div>

                {incompleteCriteria.length > 0 ? (
                  <div className="readiness-list">
                    {incompleteCriteria.map((item) => (
                      <button
                        type="button"
                        className="readiness-list-item"
                        key={item.code}
                          onClick={() => navigate(item.route || `/${item.code.toLowerCase()}`)}
                      >
                        <span>
                          <strong>{item.code}</strong> - {item.label}
                        </span>
                        <Badge bg="warning" text="dark">
                          perlu dilengkapi
                        </Badge>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="readiness-complete-note">
                    Semua kriteria sudah memiliki data.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

      </Container>
    </DashboardLayout>
  );
}

export default Dashboard;
